// frontend/src/hooks/useCompanyParams.js
import { useState, useEffect } from 'react';
import api from '../services/api';

const CACHE_KEY = 'hp_company_params';

const getCachedCompanyParams = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) { /* ignore */ }
  return null;
};

const setCachedCompanyParams = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (e) { /* ignore */ }
};

const DEFAULT_COMPANY = {
  nom: 'HoraPilot',
  adresse: '', ville: '', pays: '', code_postal: '',
  telephone: '', email: '', site_web: '', rccm: '', nif: '',
  description: '', logo_url: null, logo_initiales: 'HP', couleur_logo: '#88b9c5'
};

const useCompanyParams = () => {
  const [companyInfo, setCompanyInfo] = useState(
    () => getCachedCompanyParams() || DEFAULT_COMPANY
  );
  const [loading, setLoading] = useState(!getCachedCompanyParams());

  const loadCompanyParams = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.parametres.getEntreprise();
      if (response.success && response.data) {
        setCompanyInfo(response.data);
        setCachedCompanyParams(response.data);
      }
    } catch (error) {
      console.error('❌ [Hook] Erreur chargement paramètres entreprise:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const updateCompanyParams = async (data) => {
    try {
      const response = await api.parametres.updateEntreprise(data);
      if (response.success) {
        await loadCompanyParams();
        triggerCompanyParamsUpdate();
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const uploadLogo = async (file) => {
    try {
      const response = await api.parametres.uploadLogoEntreprise(file);
      if (response.success) {
        await loadCompanyParams();
        triggerCompanyParamsUpdate();
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const deleteLogo = async () => {
    try {
      const response = await api.parametres.deleteLogoEntreprise();
      if (response.success) {
        await loadCompanyParams();
        triggerCompanyParamsUpdate();
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyInfo(prev => ({ ...prev, [field]: value }));
  };

  const triggerCompanyParamsUpdate = () => {
    window.dispatchEvent(new CustomEvent('companyParamsUpdated'));
  };

  useEffect(() => {
    // Charger silencieusement si cache dispo, normalement sinon
    const hasCachedData = !!getCachedCompanyParams();
    loadCompanyParams(!hasCachedData ? false : true);

    const handleUpdate = () => loadCompanyParams(true);
    window.addEventListener('companyParamsUpdated', handleUpdate);
    return () => window.removeEventListener('companyParamsUpdated', handleUpdate);
  }, []);

  return {
    companyInfo, loading,
    handleInputChange, updateCompanyParams,
    uploadLogo, deleteLogo, loadCompanyParams
  };
};

export default useCompanyParams;